import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useRef } from 'react';

import { Button, Text } from '@revelio/ui';

import type { RowLimitResultsByInstanceId } from '../api/data-builder-row-limit.types';
import { useDataBuilderQuery } from '../api/useDataBuilderQuery';
import { DatasetSwitcherItem } from '../components/ui/DatasetSwitcherItem';
import {
  selectActiveInstanceId,
  selectConfigByInstance,
  selectDatasetInstances,
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../data-builder.store';
import {
  buildDatasetInstanceLabelMap,
  buildDatasetLabelMap,
} from '../datasets/dataset-instance-labels';
import { getDeliverableColumnConfig } from '../datasets/deliverable-column-config';
import { seedInstanceDefaults } from '../datasets/initial-instance-config';
import {
  getUnitOfAnalysisConsensus,
  getUoaDefaultForInstance,
} from '../unit-of-analysis-defaulting';

type Step2DatasetSwitcherProps = {
  instanceIdsWithErrors: Set<string>;
  showValidationErrors: boolean;
  rowLimitResultsByInstanceId: RowLimitResultsByInstanceId;
  isRowLimitLoading: boolean;
};

export const Step2DatasetSwitcher = ({
  instanceIdsWithErrors,
  showValidationErrors,
  rowLimitResultsByInstanceId,
  isRowLimitLoading,
}: Step2DatasetSwitcherProps) => {
  const { data } = useDataBuilderQuery();
  const instances = useDataBuilderSelector(selectDatasetInstances);
  const activeInstanceId = useDataBuilderSelector(selectActiveInstanceId);
  const configByInstance = useDataBuilderSelector(selectConfigByInstance);
  const {
    setActiveInstanceId,
    addDatasetInstance,
    removeDatasetInstance,
    renameDatasetInstance,
    setUnitOfAnalysis,
    setUnitOfAnalysisSelections,
    setCompanySelections,
    setCompanySets,
    setCompanyMapper,
  } = useDataBuilderActions();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const lastAutoActivatedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const firstId = instances[0]?.instanceId;
    if (activeInstanceId !== null || firstId === undefined) return;
    if (lastAutoActivatedIdRef.current === firstId) return;
    lastAutoActivatedIdRef.current = firstId;
    setActiveInstanceId(firstId);
  }, [activeInstanceId, instances, setActiveInstanceId]);

  const labelMap = buildDatasetLabelMap(data?.datasets);
  const instanceLabels = buildDatasetInstanceLabelMap(instances, labelMap);
  const items = instances.map((inst) => ({
    id: inst.instanceId,
    label: instanceLabels[inst.instanceId] ?? inst.datasetKey,
  }));

  if (items.length === 0) {
    return null;
  }

  const handleAddDataset = (datasetKey: string) => {
    const existingInstanceIds = instances.map((inst) => inst.instanceId);
    const deliverableConfig = getDeliverableColumnConfig(
      data?.deliverablesConfig ?? {},
      datasetKey
    );
    // Seed file/column defaults at instance-creation time so the duplicated
    // dataset doesn't have to wait on a Step 2 effect to populate them.
    const initialConfig =
      deliverableConfig !== undefined
        ? seedInstanceDefaults(deliverableConfig)
        : undefined;
    const newId = addDatasetInstance(datasetKey, initialConfig);

    const consensus = getUnitOfAnalysisConsensus(
      configByInstance,
      existingInstanceIds
    );
    if (consensus !== null) {
      const availableValues =
        deliverableConfig?.unitOfAnalysis.options.map((o) => o.value) ?? [];
      const uoaDefault = getUoaDefaultForInstance(consensus, availableValues);
      if (uoaDefault !== null) {
        setUnitOfAnalysis(newId, uoaDefault.unitOfAnalysis);
        setUnitOfAnalysisSelections(newId, uoaDefault.unitOfAnalysisSelections);

        if (uoaDefault.unitOfAnalysis === 'company') {
          const sourceConfig = Object.values(configByInstance).find(
            (c) => c.unitOfAnalysis === 'company'
          );
          if (sourceConfig) {
            if (sourceConfig.companySets !== null) {
              setCompanySets(newId, sourceConfig.companySets);
            } else if (Object.keys(sourceConfig.companySelections).length > 0) {
              setCompanySelections(newId, sourceConfig.companySelections);
            }
            if (sourceConfig.companyMapper !== null) {
              setCompanyMapper(newId, sourceConfig.companyMapper);
            }
          }
        }
      }
    }

    setActiveInstanceId(newId);
    onClose();
  };

  const availableDatasets = (data?.datasets ?? []).filter(
    (ds) => !ds.isDisabled
  );

  return (
    <Box
      data-testid="data-builder-dataset-switcher"
      borderRightWidth="1px"
      borderRightColor="gray.200"
      minWidth="280px"
      maxWidth="320px"
      flexShrink={0}
      height="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Box flex={1} minHeight={0} overflow="auto">
        <Box borderBottomWidth="1px" borderBottomColor="gray.200">
          {items.map((item) => (
            <DatasetSwitcherItem
              key={item.id}
              id={item.id}
              label={item.label}
              isActive={item.id === activeInstanceId}
              hasError={
                showValidationErrors && instanceIdsWithErrors.has(item.id)
              }
              rowLimitInfo={rowLimitResultsByInstanceId[item.id]}
              rowLimitLoading={
                isRowLimitLoading &&
                rowLimitResultsByInstanceId[item.id] === undefined
              }
              onClick={() => setActiveInstanceId(item.id)}
              onRename={(name) => renameDatasetInstance(item.id, name)}
              onDelete={() => removeDatasetInstance(item.id)}
            />
          ))}
        </Box>

        <Box padding="2">
          <Popover placement="bottom" isOpen={isOpen} onClose={onClose}>
            <PopoverTrigger>
              <div>
                <Button
                  data-testid="data-builder-add-dataset"
                  color="lightBlue"
                  variant="ghost"
                  size="xs"
                  onClick={onToggle}
                  style={{ width: '100%' }}
                >
                  + Add Dataset
                </Button>
              </div>
            </PopoverTrigger>
            <PopoverContent width="240px">
              <PopoverBody padding={0} maxHeight="280px" overflow="auto">
                {availableDatasets.map((ds) => (
                  <Flex
                    key={ds.id}
                    data-testid={`data-builder-add-dataset-option-${ds.id}`}
                    padding="8px 12px"
                    cursor="pointer"
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => handleAddDataset(ds.id)}
                  >
                    <Text fontSize="sm">{ds.label}</Text>
                  </Flex>
                ))}
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Box>
      </Box>
    </Box>
  );
};
